import type { ActionTree } from 'vuex';
import type { AxiosResponse } from 'axios';

import { cloneDeep, get } from 'lodash-es';
import { setItem } from '@odin/utils';
import { useMessages } from '@odin/services';
import type {
  File,
  BillingProfile,
  DomainProfile,
  PaymentOptionGroup,
  CategoryProfileType,
  CommonProfile,
  DomainProfileExtended,
} from '@/types';
import type {
  AppState,
  SetRequiredFieldsPayload,
} from './const';
import getInitialState from './appState';
import { StorageKeys } from '@/services/const';
import {
  getBillingProfiles,
  getDomainProfiles,
  getPaymentOptions,
  getRequiredDpFields,
  removeFile,
  updateBillingProfile,
  updateDomainProfile,
} from '@/services/cart/cart-api.service';
import { fetchUserProfile, userDomains } from '@/services/user-api.service';
import { normalizeDomainProfile } from '@/services/profiles.service';
import { OPTIONS_ORDER } from '@/components/paymentOptions/const';
import {StateMutations, StoreActions} from '@/store/const.enum';

// Do not move the useMessages() initialization ouf ot the methods, the service is not yet setup.

export const actions: ActionTree<AppState, AppState> = {
  [StoreActions.RESET_STATE]({ commit }) {
    commit(StateMutations.MERGE_STATE, getInitialState());
  },

  [StoreActions.REMOVE_FILE](
    { state, commit },
    { file, path }: { file: File; path: string }
  ) {
    const files = get(state, path);
    if (!files) {
      return Promise.reject('exception.files_state_missing');
    }
    const serverFileName = file.serverFileName;
    const index = files.indexOf(file);
    return removeFile(serverFileName).then(() => {
      if (index > -1) {
        commit(StateMutations.REMOVE_STATE, {
          path,
          prop: index,
        });
      }
    });
  },

  [StoreActions.FETCH_REQ_FIELDS](
    { getters, commit },
    profileCountry?: string
  ) {
    const path = 'required_fields';
    const tlds = getters.tlds;
    if (!tlds || !tlds.length) {
      // die sillently as user does not need required fields
      // if they do not have domains in cart
      return;
    }
    const cached = getters.requiredFields(undefined, profileCountry);
    if (cached) {
      return Promise.resolve();
    }
    return getRequiredDpFields(tlds, profileCountry)
      .then((response: AxiosResponse) => {
        commit(StateMutations.SET_REQUIRED_FIELDS, {
          tlds,
          profileCountry,
          requiredFields: response.data,
        } as SetRequiredFieldsPayload);
      })
      .catch((err) => {
        const { handleUnstructuredError } = useMessages();
        handleUnstructuredError(path, err);
        return Promise.reject(err);
      });
  },

  [StoreActions.SET_USER_STEP]({ commit, state }, { step }) {
    commit(StateMutations.SET_STATE, { prop: 'userStep', value: step });

    const uuid = state.cart.uuid;
    setItem(StorageKeys.userStep, { step, uuid }, true);
  },

  [StoreActions.FETCH_BILLING_PROFILES]({ commit }) {
    return getBillingProfiles()
      .then(response => {
        const profiles: BillingProfile[] = response.data;
        commit(StateMutations.SET_STATE, {
          prop: 'billingProfiles',
          value: profiles,
        });
        return Promise.resolve(profiles);
      })
      .catch((err) => {
        const { handleUnstructuredError } = useMessages();
        handleUnstructuredError('billingProfiles', err);
        return Promise.reject(err);
      });
  },

  [StoreActions.ENSURE_DOMAIN_PROFILES]({ dispatch, state }) {
    const profilesPresent = state.domainProfiles?.length;
    return profilesPresent
      ? Promise.resolve(state.domainProfiles)
      : dispatch(StoreActions.FETCH_DOMAIN_PROFILES);
  },

  [StoreActions.FETCH_DOMAIN_PROFILES]({ commit }) {
    return getDomainProfiles()
      .then(response => {
        let profiles: DomainProfileExtended[] = response.data;
        profiles = profiles.map(
          (profile: DomainProfile) => normalizeDomainProfile(profile)!
        );
        commit(StateMutations.SET_STATE, {
          prop: 'domainProfiles',
          value: profiles,
        });
        return Promise.resolve(profiles);
      })
      .catch((err) => {
        const { handleUnstructuredError } = useMessages();
        handleUnstructuredError('domainProfiles', err);
        return Promise.reject(err);
      });
  },

  async [StoreActions.ENSURE_USER_DOMAINS]({ state, commit }) {
    if (state.userDomains?.length) {
      return state.userDomains;
    }
    try {
      const data = await userDomains();
      const domains = data?.domains;
      commit(StateMutations.SET_STATE, {
        prop: 'userDomains',
        value: domains,
      });
      return domains;
    } catch (err) {
      const { handleUnstructuredError } = useMessages();
      handleUnstructuredError('general', err);
      return Promise.reject(err);
    }
  },

  [StoreActions.UPDATE_PROFILE](
    { commit },
    { profile, type }: { profile: CommonProfile; type: CategoryProfileType }
  ): Promise<CommonProfile> {
    profile = cloneDeep(profile);
    if (type === 'domain') {
      profile = normalizeDomainProfile(profile as DomainProfile)!;
    }

    const updateMethods: Record<CategoryProfileType, (profile: any) => Promise<AxiosResponse>> = {
      domain: updateDomainProfile,
      billing: updateBillingProfile
    };

    return updateMethods[type](profile).then(() => {
      commit(StateMutations.UPDATE_PROFILE, {
        type,
        profile,
      });
      return Promise.resolve(profile);
    });
  },

  [StoreActions.FETCH_USER_PROFILE]({ state, getters }) {
    const userProfile = state.userProfile;

    if (userProfile) {
      return Promise.resolve(userProfile);
    } else if (getters.loggedIn) {
      return fetchUserProfile();
    } else {
      return Promise.resolve();
    }
  },

  [StoreActions.SET_PAYMENT_OPTIONS]({commit}, {paymentOptions} : {paymentOptions: PaymentOptionGroup[]}) {
    const options = sortAndFilterOptions(paymentOptions);

    return commit(StateMutations.SET_STATE, {
      prop: 'paymentOptions',
      value: options,
    });
  },

  [StoreActions.FETCH_PAYMENT_OPTIONS]({ commit }) {
    return getPaymentOptions().then(response => {
      const options = sortAndFilterOptions(response.data);

      commit(StateMutations.SET_STATE, {
        prop: 'paymentOptions',
        value: options,
      });
    });
  },
};

function sortAndFilterOptions(
  paymentOptions: PaymentOptionGroup[]
): PaymentOptionGroup[] {
  const sortedOptions: PaymentOptionGroup[] = [];
  const unlistedOptions: PaymentOptionGroup[] = [];
  paymentOptions.forEach((option: PaymentOptionGroup) => {
    const index = OPTIONS_ORDER.indexOf(option.id);
    if (index > -1) {
      sortedOptions[index] = option;
    } else {
      unlistedOptions.push(option);
    }
  });
  return sortedOptions
    .filter(option => !!option)
    .concat(unlistedOptions);
}
